import React, { useState, useEffect, useMemo } from 'react';
import { Search, MessageSquare, CheckCircle, Clock, X, Send, Image as ImageIcon, ExternalLink, AlertCircle, Phone, Mail, User } from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';
import { CountryBadge } from '../components/SearchableCountrySelect';

const priorityConfig = {
  Urgent: { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', dot: 'bg-rose-500' },
  High: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', dot: 'bg-orange-500' },
  Medium: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', dot: 'bg-amber-500' },
  Low: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', dot: 'bg-blue-500' }
};

const SupportCenter = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL' | 'open' | 'pending' | 'resolved'
  const [roleFilter, setRoleFilter] = useState('ALL'); // 'ALL' | 'MANAGER' | 'BD' | 'AGENCY' | 'HOST' | 'USER'
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/support/tickets`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setTickets(response.data.data);
        if (selectedTicket) {
          const updated = response.data.data.find(t => t.id === selectedTicket.id);
          setSelectedTicket(updated || null);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;
    setReplyLoading(true);
    try {
      const res = await axios.post(`${constant.API_BASE_URL}/admin/business/support/tickets/${selectedTicket.id}/reply`, {
        message: replyText
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (res.data.success) {
        setReplyText('');
        fetchTickets();
      }
    } catch (err) {
      alert("Failed to submit reply");
    } finally {
      setReplyLoading(false);
    }
  };

  const handleResolveTicket = async (id, status = 'Resolved') => {
    try {
      setSelectedTicket(prev => prev && prev.id === id ? { ...prev, status } : prev);
      setTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));

      await axios.put(`${constant.API_BASE_URL}/admin/business/support/tickets/${id}/status`, { status }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      await axios.post(`${constant.API_BASE_URL}/admin/business/support/tickets/${id}/reply`, {
        message: `[Support Notice] Status marked as: ${status}`,
        status
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      fetchTickets();
    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    }
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      // 1. Status Filter
      if (statusFilter !== 'ALL') {
        const s = (t.status || '').toLowerCase();
        if (statusFilter === 'resolved' && (s !== 'resolved' && s !== 'solved')) return false;
        if (statusFilter === 'pending' && (s !== 'pending' && s !== 'in progress' && s !== 'inprogress')) return false;
        if (statusFilter === 'open' && s !== 'open') return false;
      }

      // 2. Role Filter (Manager to Admin, BD, Agency, Host, User)
      if (roleFilter !== 'ALL') {
        const ut = (t.user_type || 'USER').toUpperCase();
        if (roleFilter === 'MANAGER' && ut !== 'MANAGER') return false;
        if (roleFilter === 'BD' && ut !== 'BD') return false;
        if (roleFilter === 'AGENCY' && ut !== 'AGENCY') return false;
        if (roleFilter === 'HOST' && ut !== 'HOST') return false;
        if (roleFilter === 'USER' && (ut !== 'USER' && ut !== 'APP_USER')) return false;
      }

      // 3. Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const ticketId = (t.ticket_id || '').toLowerCase();
        const subject = (t.subject || '').toLowerCase();
        const category = (t.category || '').toLowerCase();
        const msg = (t.message || '').toLowerCase();
        const userName = `${t.first_name || ''} ${t.last_name || ''}`.toLowerCase();
        const xoxoId = String(t.xoxo_id || t.app_user_id || '').toLowerCase();
        const contact = (t.contact || t.email || '').toLowerCase();

        return (
          ticketId.includes(q) ||
          subject.includes(q) ||
          category.includes(q) ||
          msg.includes(q) ||
          userName.includes(q) ||
          xoxoId.includes(q) ||
          contact.includes(q)
        );
      }

      return true;
    });
  }, [tickets, statusFilter, roleFilter, searchQuery]);

  const resolveImageUrl = (img) => {
    if (!img) return '';
    if (img.startsWith('http://') || img.startsWith('https://')) return img;
    const baseOrigin = constant.API_BASE_URL ? constant.API_BASE_URL.replace(/\/api$/, '') : 'https://sandbox.xoxolite.com/xoxo';
    const cleanPath = img.startsWith('/') ? img : `/${img}`;
    return `${baseOrigin}${cleanPath}`;
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-3xl font-extrabold text-slate-800">Ticket & Support Center</h2>
        <p className="text-slate-500 mt-1">Review tickets raised by users in the mobile app or web, view attached screenshots, and reply in real-time.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Ticket List Column */}
        <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col h-[700px]">
          <div className="p-4 border-b border-slate-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-slate-800">Support Tickets</h3>
              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-extrabold text-xs">
                {filteredTickets.length}
              </span>
            </div>
            <button onClick={fetchTickets} className="text-xs text-pink-600 font-bold hover:underline cursor-pointer">
              Refresh
            </button>
          </div>

          {/* Search Box */}
          <div className="p-3 border-b border-slate-100 bg-slate-50/50">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Search ticket ID, user, subject..."
                className="w-full pl-9 pr-3 py-2 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-pink-500/20 transition font-medium"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600">
                  <X size={14} />
                </button>
              )}
            </div>
          </div>

          {/* Role Filter Tabs (Manager Complaints, BD, Agency, Host, User) */}
          <div className="flex items-center gap-1 p-2 bg-slate-100/70 border-b border-slate-200 overflow-x-auto text-[11px] font-bold">
            {[
              { id: 'ALL', label: 'All Roles' },
              { id: 'MANAGER', label: '👑 Manager to Admin', count: tickets.filter(t => (t.user_type || '').toUpperCase() === 'MANAGER').length },
              { id: 'BD', label: '💼 BD', count: tickets.filter(t => (t.user_type || '').toUpperCase() === 'BD').length },
              { id: 'AGENCY', label: '🏢 Agency', count: tickets.filter(t => (t.user_type || '').toUpperCase() === 'AGENCY').length },
              { id: 'HOST', label: '🎤 Host', count: tickets.filter(t => (t.user_type || '').toUpperCase() === 'HOST').length },
              { id: 'USER', label: '👤 User', count: tickets.filter(t => (t.user_type || '').toUpperCase() === 'USER' || !t.user_type).length },
            ].map(r => (
              <button
                key={r.id}
                onClick={() => setRoleFilter(r.id)}
                className={`px-2.5 py-1 rounded-lg text-center whitespace-nowrap transition cursor-pointer flex items-center gap-1.5 ${roleFilter === r.id
                    ? 'bg-slate-900 text-white shadow-sm font-extrabold'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
                  }`}
              >
                <span>{r.label}</span>
                {r.count !== undefined && r.count > 0 && (
                  <span className={`px-1.5 py-0.2 rounded-full text-[9px] ${roleFilter === r.id ? 'bg-pink-500 text-white' : 'bg-slate-200 text-slate-700'
                    }`}>
                    {r.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Status Tabs */}
          <div className="flex items-center gap-1 p-2 bg-slate-50 border-b border-slate-100 text-[11px] font-bold">
            {['ALL', 'open', 'pending', 'resolved'].map(f => (
              <button
                key={f}
                onClick={() => setStatusFilter(f)}
                className={`flex-1 py-1 rounded-lg text-center capitalize transition cursor-pointer ${statusFilter === f
                    ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80 font-black'
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
              >
                {f}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-10 text-center flex-1 flex flex-col items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mb-2"></div>
              <p className="text-slate-500 text-xs">Loading queue...</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-10 text-center text-slate-400 flex-1 flex flex-col items-center justify-center">
              <MessageSquare size={36} className="mb-2 opacity-35" />
              <p className="text-sm font-semibold">No tickets found.</p>
              {searchQuery && <p className="text-xs text-slate-400 mt-1">Try clearing your search query.</p>}
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 divide-y divide-slate-50">
              {filteredTickets.map((t) => {
                const s = (t.status || 'open').toLowerCase();
                const isResolved = s === 'resolved' || s === 'solved';
                const isPending = s === 'pending' || s === 'in progress' || s === 'inprogress';
                const p = priorityConfig[t.priority] || priorityConfig.Medium;
                const ut = (t.user_type || 'USER').toUpperCase();

                return (
                  <div
                    key={t.id}
                    onClick={() => setSelectedTicket(t)}
                    className={`p-4 cursor-pointer hover:bg-slate-50/80 transition relative ${selectedTicket?.id === t.id ? 'bg-pink-50/50 border-r-4 border-pink-500' : ''
                      }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 font-mono font-bold text-[10px]">
                          {t.ticket_id || `#${t.id.substring(0, 8).toUpperCase()}`}
                        </span>
                        {ut === 'MANAGER' && (
                          <span className="px-2 py-0.5 rounded-md text-[9px] font-black bg-amber-100 text-amber-900 border border-amber-300 shadow-sm flex items-center gap-1">
                            👑 MANAGER
                          </span>
                        )}
                        {ut === 'BD' && (
                          <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-indigo-100 text-indigo-900 border border-indigo-200 flex items-center gap-1">
                            💼 BD
                          </span>
                        )}
                        {ut === 'AGENCY' && (
                          <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-sky-100 text-sky-900 border border-sky-200 flex items-center gap-1">
                            🏢 AGENCY
                          </span>
                        )}
                        {ut === 'HOST' && (
                          <span className="px-2 py-0.5 rounded-md text-[9px] font-bold bg-rose-100 text-rose-900 border border-rose-200 flex items-center gap-1">
                            🎤 HOST
                          </span>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${isResolved ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                          isPending ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                            'bg-rose-50 text-rose-600 border border-rose-200'
                        }`}>
                        {isResolved ? 'Resolved' : isPending ? 'In Progress' : 'Open'}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 mt-2">
                      <span className={`px-2 py-0.5 rounded-md text-[9px] font-bold border ${p.bg} ${p.text} ${p.border}`}>
                        {t.priority || 'Medium'}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-slate-50 text-slate-600 font-semibold text-[10px] border border-slate-100">
                        {t.category || t.subject || 'General'}
                      </span>
                      {t.images && t.images.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded-md bg-pink-50 text-pink-600 text-[10px] font-bold flex items-center gap-0.5" title={`${t.images.length} attachment(s)`}>
                          <ImageIcon size={10} /> {t.images.length}
                        </span>
                      )}
                    </div>

                    <h4 className="font-bold text-slate-800 text-xs mt-2 line-clamp-1">{t.subject}</h4>
                    <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{t.message}</p>

                    <div className="flex justify-between items-center mt-3 text-[10px] text-slate-400">
                      <div className="flex items-center gap-1.5 truncate max-w-[160px]">
                        <span className="font-medium text-slate-600 truncate">{t.first_name || 'User'} {t.last_name || ''}</span>
                        {t.xoxo_id && (
                          <span className="text-[9px] text-pink-600 font-mono font-bold">ID: {t.xoxo_id}</span>
                        )}
                        {t.country && <CountryBadge country={t.country} className="text-[10px] px-1 py-0" />}
                      </div>
                      <span className="shrink-0">{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Chat / Ticket Reply Panel */}
        <div className="lg:col-span-2 flex flex-col bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden h-[700px]">
          {selectedTicket ? (
            <>
              {/* Header */}
              <div className="p-5 border-b border-slate-100 flex flex-col gap-3 shrink-0 bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="px-2.5 py-1 rounded-lg bg-pink-50 text-pink-600 font-mono font-extrabold text-xs">
                      {selectedTicket.ticket_id || `#${selectedTicket.id.substring(0, 8).toUpperCase()}`}
                    </span>
                    <h3 className="font-bold text-slate-800 text-base">{selectedTicket.subject}</h3>
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase ${(selectedTicket.status || '').toLowerCase().includes('resolve')
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                        : (selectedTicket.status || '').toLowerCase().includes('progress') || (selectedTicket.status || '').toLowerCase().includes('pending')
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-rose-100 text-rose-800 border border-rose-300'
                      }`}>
                      {selectedTicket.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    {(selectedTicket.status || '').toLowerCase().includes('resolve') ? (
                      <div className="flex items-center gap-1.5">
                        <span className="px-3 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200 font-black text-xs flex items-center gap-1">
                          <CheckCircle size={14} className="text-emerald-600" /> Resolved
                        </span>
                        <button
                          onClick={() => handleResolveTicket(selectedTicket.id, 'Open')}
                          className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition cursor-pointer"
                        >
                          Reopen
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => handleResolveTicket(selectedTicket.id, 'In Progress')}
                          className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 font-bold rounded-xl text-xs transition cursor-pointer"
                        >
                          Mark In Progress
                        </button>
                        <button
                          onClick={() => handleResolveTicket(selectedTicket.id, 'Resolved')}
                          className="px-3.5 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 active:scale-95 text-white font-bold rounded-xl text-xs transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                        >
                          <CheckCircle size={14} /> Resolve Ticket
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg cursor-pointer ml-1"
                    >
                      <X size={18} />
                    </button>
                  </div>
                </div>

                {/* Meta details banner */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                  <div className="flex items-center gap-1.5 font-medium text-slate-700">
                    <User size={13} className="text-pink-600" />
                    <span>{selectedTicket.first_name || 'User'} {selectedTicket.last_name || ''}</span>
                    {(() => {
                      const ut = (selectedTicket.user_type || 'USER').toUpperCase();
                      if (ut === 'MANAGER') return <span className="text-[10px] font-black bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded shadow-sm">👑 MANAGER ESCALATION</span>;
                      if (ut === 'BD') return <span className="text-[10px] font-bold bg-indigo-100 text-indigo-900 border border-indigo-200 px-2 py-0.5 rounded">💼 BD</span>;
                      if (ut === 'AGENCY') return <span className="text-[10px] font-bold bg-sky-100 text-sky-900 border border-sky-200 px-2 py-0.5 rounded">🏢 AGENCY</span>;
                      if (ut === 'HOST') return <span className="text-[10px] font-bold bg-rose-100 text-rose-900 border border-rose-200 px-2 py-0.5 rounded">🎤 HOST</span>;
                      return null;
                    })()}
                    {selectedTicket.xoxo_id && (
                      <span className="font-mono font-bold text-pink-600 text-[11px] bg-pink-50 px-1.5 py-0.5 rounded">
                        ID: {selectedTicket.xoxo_id}
                      </span>
                    )}
                    {selectedTicket.country && <CountryBadge country={selectedTicket.country} />}
                  </div>

                  {selectedTicket.contact && (
                    <div className="flex items-center gap-1 font-medium text-slate-600">
                      <Phone size={12} className="text-slate-400" />
                      <span>{selectedTicket.contact}</span>
                    </div>
                  )}

                  {selectedTicket.email && (
                    <div className="flex items-center gap-1 font-medium text-slate-600">
                      <Mail size={12} className="text-slate-400" />
                      <span>{selectedTicket.email}</span>
                    </div>
                  )}

                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-[11px] font-bold text-slate-400">Category:</span>
                    <span className="font-bold text-slate-700">{selectedTicket.category || selectedTicket.subject || 'General'}</span>
                    <span className="text-slate-300">•</span>
                    <span className="text-[11px] font-bold text-slate-400">Priority:</span>
                    <span className="font-extrabold text-pink-600">{selectedTicket.priority || 'Medium'}</span>
                  </div>
                </div>
              </div>

              {/* Thread Chat Pane */}
              <div className="flex-1 p-6 overflow-y-auto bg-slate-50 space-y-6">
                {/* Original Message Box */}
                <div className="flex flex-col items-start max-w-[85%]">
                  <div className="bg-white p-5 rounded-2xl rounded-tl-none shadow-sm border border-slate-100 space-y-3">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <span className="font-bold text-slate-800 text-xs flex items-center gap-1.5">
                        <AlertCircle size={14} className="text-pink-600" /> Initial Issue Description
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {new Date(selectedTicket.created_at).toLocaleString()}
                      </span>
                    </div>

                    <p className="text-sm text-slate-800 font-medium whitespace-pre-wrap leading-relaxed">
                      {selectedTicket.message || selectedTicket.description || 'No description provided.'}
                    </p>

                    {/* Screenshot attachments if any */}
                    {selectedTicket.images && selectedTicket.images.length > 0 && (
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <ImageIcon size={12} /> Attached Evidence ({selectedTicket.images.length})
                        </p>
                        <div className="flex flex-wrap gap-2.5">
                          {selectedTicket.images.map((imgUrl, idx) => (
                            <div key={idx} className="relative group">
                              <img
                                src={resolveImageUrl(imgUrl)}
                                alt={`Attachment ${idx + 1}`}
                                className="w-20 h-20 object-cover rounded-xl border border-slate-200 shadow-sm cursor-pointer hover:border-pink-500 transition-all hover:scale-105"
                                onClick={() => setPreviewImage(resolveImageUrl(imgUrl))}
                              />
                              <a
                                href={resolveImageUrl(imgUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className="absolute bottom-1 right-1 p-1 bg-black/60 rounded-md text-white opacity-0 group-hover:opacity-100 transition"
                                title="Open full image"
                              >
                                <ExternalLink size={10} />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reply Threads */}
                {selectedTicket.replies && selectedTicket.replies.map((reply, i) => {
                  const senderName = reply.sender_name || reply.sender || 'Support Team';
                  const isAdmin = senderName.toLowerCase().includes('admin') || senderName.toLowerCase().includes('support') || reply.sender === 'System';
                  const timeStr = reply.time || reply.timestamp;

                  return (
                    <div key={i} className={`flex flex-col max-w-[85%] ${isAdmin ? 'ml-auto items-end' : 'items-start'}`}>
                      <div className={`p-4 rounded-2xl shadow-sm border ${isAdmin ? 'bg-slate-900 text-white border-slate-900 rounded-tr-none' : 'bg-white text-slate-800 border-slate-100 rounded-tl-none'
                        }`}>
                        <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap">{reply.message}</p>
                        <span className={`text-[10px] mt-2 block ${isAdmin ? 'text-slate-400' : 'text-slate-400'}`}>
                          {senderName} • {timeStr ? new Date(timeStr).toLocaleString() : ''}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Input Footer */}
              <form onSubmit={handleSendReply} className="p-4 border-t border-slate-100 flex gap-3 shrink-0 bg-white">
                <input
                  required
                  placeholder="Type official support reply to the user..."
                  className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition text-sm font-medium"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                />
                <button
                  type="submit"
                  disabled={replyLoading}
                  className="px-6 py-3 bg-pink-600 hover:bg-pink-700 active:scale-95 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 shadow-lg shadow-pink-200 disabled:opacity-50 cursor-pointer"
                >
                  <Send size={16} /> Send Reply
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8">
              <MessageSquare size={54} className="opacity-20 mb-3" />
              <h4 className="font-extrabold text-slate-600 text-lg">No Ticket Selected</h4>
              <p className="text-sm mt-1 max-w-sm">Select any user ticket from the queue on the left to inspect logs, view screenshots, and send official replies.</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-3xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
            <img src={previewImage} alt="Full preview" className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl" />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 p-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition shadow-lg"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportCenter;
